import React, { useCallback, useMemo, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow
} from '@this/src/components/shared/ui/data_displays/table';
import { Button } from '@this/components/shared/ui/inputs/button';
import { Checkbox } from '@this/src/components/shared/ui/inputs/checkbox';
import { Fetcher } from '@this/src/util';
import Notification from '@this/src/notification';
import type HotelOrderItemAgodaTodo from '@this/src/domain/arrangement/hotel_order_item_agoda_todo';
import ItemsTableRow from './items_table_row';

interface Props {
  todos: HotelOrderItemAgodaTodo[];
  totalCount: number;
  totalPage: number;
  page: number;
  showDescription: boolean;
  isDiff: boolean;
  isNotDiff: boolean;
  fetchTodos: () => void;
  setPage: (page: number) => void;
}

const ItemsResultTable: React.FC<Props> = ({
  todos,
  totalCount,
  totalPage,
  page,
  showDescription,
  isDiff,
  isNotDiff,
  fetchTodos,
  setPage
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedOrderItemIds, setSelectedOrderItemIds] = useState<number[]>([]);

  const bulkTargetTodos = useMemo(() => todos.filter(todo => todo.orderItemId && !todo.inventoriedAt), [todos]);
  const bulkInventoryDisabled = useMemo(() => isDiff || !isNotDiff, [isDiff, isNotDiff]);
  const bulkInventoryEmptyTargets = useMemo(() => todos.filter(todo => todo.orderItemId).length === 0, [todos]);

  const handleToggleAll = useCallback(
    (checked: boolean) => {
      setSelectedOrderItemIds(checked ? bulkTargetTodos.map(todo => todo.orderItemId as number) : []);
    },
    [bulkTargetTodos]
  );

  const handleToggle = useCallback((checked: boolean, orderItemId: number) => {
    setSelectedOrderItemIds(current =>
      checked ? [...current, orderItemId] : current.filter(id => id !== orderItemId)
    );
  }, []);

  const handleBulkInventory = useCallback(
    async (orderItemIds: number[]) => {
      if (loading || bulkInventoryDisabled || bulkInventoryEmptyTargets) return;

      try {
        setLoading(true);
        await Fetcher.put('/arrangement/auto_reservation_inventory/agoda_hotel/items/bulk_inventory', {
          order_item_ids: orderItemIds
        });
        fetchTodos();
        setSelectedOrderItemIds([]);
        Notification.success('一括棚卸を実行しました');
      } catch {
        Notification.error('一括棚卸に失敗しました');
      } finally {
        setLoading(false);
      }
    },
    [loading, bulkInventoryDisabled, bulkInventoryEmptyTargets, fetchTodos]
  );

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div>検索結果:{totalCount}件</div>
        <Button
          onClick={() => handleBulkInventory(selectedOrderItemIds)}
          disabled={bulkInventoryDisabled || bulkInventoryEmptyTargets}
          loading={loading}
        >
          一括棚卸し
        </Button>
        {bulkInventoryDisabled ? (
          <div style={{ color: 'gray' }}>一括棚卸は差異なしの検索時のみ利用できます</div>
        ) : bulkInventoryEmptyTargets ? (
          <div style={{ color: 'gray' }}>一括棚卸の対象がありません</div>
        ) : null}
      </div>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>
              <Checkbox
                checked={bulkTargetTodos.length > 0 && selectedOrderItemIds.length === bulkTargetTodos.length}
                disabled={bulkInventoryDisabled || bulkInventoryEmptyTargets}
                onChange={e => handleToggleAll(e.target.checked)}
              />
            </TableCell>
            <TableCell />
            <TableCell>予約番号</TableCell>
            <TableCell>宿泊人数</TableCell>
            <TableCell>施設名</TableCell>
            <TableCell>施設住所</TableCell>
            <TableCell>部屋タイプ</TableCell>
            <TableCell>プラン名</TableCell>
            <TableCell>ホテルプラン選択機能</TableCell>
            <TableCell>申請者によるプランの事前チェック</TableCell>
            <TableCell>朝食有無</TableCell>
            <TableCell>チェックイン日</TableCell>
            <TableCell>チェックアウト日</TableCell>
            <TableCell>宿泊代表者名</TableCell>
            <TableCell>ステータス</TableCell>
            <TableCell>セルフサービス</TableCell>
            <TableCell>料金</TableCell>
            <TableCell>卸値の仕入商品</TableCell>
            <TableCell>棚卸ステータス</TableCell>
            <TableCell>棚卸実施者</TableCell>
            <TableCell>棚卸日時</TableCell>
            <TableCell>Trip ID</TableCell>
            <TableCell>Trace ID</TableCell>
            <TableCell>チェックイン時の注意事項</TableCell>
            <TableCell>最新のログメッセージ</TableCell>
            <TableCell>アクション</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {todos.map((todo, index) => (
            <ItemsTableRow
              key={todo.traceId || todo.fromAgodaReservation.reservationNumber || index}
              todo={todo}
              checked={todo.orderItemId != null && selectedOrderItemIds.includes(todo.orderItemId)}
              showDescription={showDescription}
              onInventory={() => fetchTodos()}
              onToggle={(checked: boolean) => {
                if (todo.orderItemId != null) handleToggle(checked, todo.orderItemId);
              }}
            />
          ))}
        </TableBody>
      </Table>
      <TablePagination
        count={totalPage}
        page={page}
        onChange={(_e: unknown, newPage: number) => setPage(newPage)}
      />
    </>
  );
};

export default ItemsResultTable;
